import * as React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Cancel';
import Add from '@material-ui/icons/Add';
import { Grid, IconButton, Typography, Paper, InputBase, MenuItem, Menu, Fade, Modal } from '@material-ui/core';
import styles from './ManageOrgTags.styles';
import SearchIcon from '@material-ui/icons/Search';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import * as Fuse from 'fuse.js';
import TextInputField from '../../Auth/TextInputField';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Button } from '../../common';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import firebase from 'firebase';

const FUSE_OPTIONS = {
  shouldSort: true,
  threshold: 0.05,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: ['name'],
};

class ManageOrgTags extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    tags: PropTypes.object,
    addedTag: PropTypes.object,
    deletedTag: PropTypes.object,
    init: PropTypes.func,
    save: PropTypes.func.isRequired,
    delete: PropTypes.func.isRequired,
    status: PropTypes.shape({
      success: PropTypes.bool,
      message: PropTypes.string,
    }),
  };
  state = {
    anchorEl: null,
    isDeleteModal: false,
    isSaveModal: false,
    order: 'asc',
    query: '',
    searchQuery: '',
    loadingTags: false,
    selectedTag: {},
    firebaseId: null,
    recomputing: false,
    recomputeMsg: null,
  };

  // Seed + apply the reserved "HB 1416" / "HB 1416 + AEP" tags off the district's
  // STAAR/EOC data. The callable enforces districtAdmin; a non-admin sees the
  // permission error here rather than a hidden button.
  handleRecompute = async () => {
    if (this.state.recomputing) return;
    this.setState({ recomputing: true, recomputeMsg: null });
    try {
      const res = await firebase.functions().httpsCallable('recomputeHb1416')({});
      const d = (res && res.data) || {};
      this.setState({
        recomputeMsg: `Tagged ${d.needsAICount || 0} students HB 1416 (${d.aepCount || 0} need an AEP) of ${d.scanned ||
          0} scanned. ${d.changed || 0} updated.`,
      });
      if (this.props.init) this.props.init(); // refresh so the new reserved tags appear
    } catch (e) {
      this.setState({ recomputeMsg: `Recompute failed: ${(e && e.message) || 'error'}` });
    } finally {
      this.setState({ recomputing: false });
    }
  };

  componentDidMount() {
    const { init, tags } = this.props;
    // Tags persist in Redux across navigation; only fetch if we don't already have
    // them, so returning to this screen doesn't re-hit the callable. The fetch is no
    // longer in the Settings loading gate, so even this first load is non-blocking.
    if (!tags || Object.keys(tags).length === 0) {
      this.setState({ loadingTags: true });
      init();
    }
  }

  componentDidUpdate(prevProps) {
    // Clear the local loading flag once the fetch lands (the tags reference changes).
    if (this.state.loadingTags && prevProps.tags !== this.props.tags) {
      this.setState({ loadingTags: false });
    }
  }

  handleSort = (event) => {
    const { order } = this.state;
    let newOrder = 'desc';
    if (order === 'desc') {
      newOrder = 'asc';
    }
    this.setState({ order: newOrder });
  };

  onSearchChange = (e) => {
    const query = e.target.value;
    // Clearing the box restores the full list immediately; otherwise the filter is
    // applied on search click / Enter (the existing behavior).
    this.setState((prev) => ({ query, searchQuery: query === '' ? '' : prev.searchQuery }));
  };

  onSearchClick = () => {
    this.setState((prev) => ({ searchQuery: prev.query }));
  };

  onSearchEnter = (e) => {
    if (e.key === 'Enter') {
      this.onSearchClick();
    }
  };
  onSortChange = () => (event) => {
    this.handleSort(event);
  };

  sortBy = (array, order) => {
    if (order == null) {
      order = 'asc';
    }

    if (order == null) {
      order = 'asc';
    }

    const orderConst = order === 'desc' ? 1 : -1;
    const orderByKey = 'name';
    if (array == null || array.length == 0) {
      return [];
    }
    if (array.length == 1) {
      return array;
    }
    return array.sort((a, b) => {
      let ele1 = a[orderByKey];
      let ele2 = b[orderByKey];
      if (!ele1 && !ele2) {
        return 0;
      }
      if (ele1 && !ele2) {
        return orderConst;
      }
      if (ele2 && !ele1) {
        return -orderConst;
      }
      if (typeof ele1 === 'string' || typeof ele2 === 'string') {
        return ele1.localeCompare(ele2) * orderConst;
      }
      return (ele1 - ele2) * orderConst;
    });

    /* for primitive array
    if(order == 'asc'){
      return array.sort().reverse();
      
    }else{
      return array.sort();
    }*/
  };

  handleSaveSubmit = (values) => {
    var data = this.props.save(this.state.firebaseId, values);
    this.handleCloseModal();
  };

  handleMenuClose = () => {
    this.setState({ anchorEl: null });
  };

  handleOpenNewModal = () => {
    this.setState({ isSaveModal: true, firebaseId: null, anchorEl: null });
  };
  handleOpenEditModal = (firebaseId) => {
    this.setState({ isSaveModal: true, firebaseId: firebaseId, anchorEl: null });
  };
  handleOpenDeleteModal = (firebaseId) => {
    this.setState({ isDeleteModal: true, firebaseId: firebaseId, anchorEl: null });
  };

  handleCloseModal = () => {
    this.setState({
      isDeleteModal: false,
      isSaveModal: false,
    });
  };

  handleConfirmDelete = () => {
    this.props.delete(this.state.firebaseId);
    this.handleCloseModal();
  };
  saveTagSchema = () => {
    return Yup.object().shape({
      name: Yup.string()
        .min(2, 'The Name should at least have 2 characters.')
        .required('Required.'),
    });
  };

  handleMenuClick = (event) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  renderEditMenu(firebaseId) {
    const { anchorEl } = this.state;
    let menuId = 'fade-menu=button' + firebaseId;
    const open = anchorEl != null && anchorEl.id == menuId;
    return (
      <div>
        <IconButton
          id={menuId}
          aria-label="More"
          aria-owns={open ? 'fade-menu' + firebaseId : undefined}
          aria-haspopup="true"
          onClick={this.handleMenuClick}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu id={menuId} anchorEl={anchorEl} open={open} onClose={this.handleMenuClose} TransitionComponent={Fade}>
          <MenuItem key="Edit" onClick={() => this.handleOpenEditModal(firebaseId)}>
            Edit
          </MenuItem>
          <MenuItem key="Delete" onClick={() => this.handleOpenDeleteModal(firebaseId)}>
            Delete
          </MenuItem>
        </Menu>
      </div>
    );
  }

  renderStatus = () => {
    const { classes, status } = this.props;
    const Icon = status.success ? CheckCircleIcon : ErrorIcon;
    if (status.message) {
      return (
        <div
          className={classNames(classes.statusMsg, {
            [classes.success]: status.success,
            [classes.error]: !status.success,
          })}
        >
          <Icon className={status.success ? classes.success : classes.error} />
          {status.message}
        </div>
      );
    }
    return null;
  };

  renderSearchBar = () => {
    const { classes } = this.props;
    return (
      <Paper>
        <InputBase
          fullWidth
          onKeyDown={this.onSearchEnter}
          onChange={this.onSearchChange}
          className={classes.input}
          placeholder="Name"
          inputProps={{ 'aria-label': 'Name' }}
          endAdornment={
            <IconButton className={classes.iconButton} aria-label="search" onClick={this.onSearchClick}>
              <SearchIcon />
            </IconButton>
          }
        />
      </Paper>
    );
  };
  renderSortIcon() {
    const { order } = this.state;
    if (order == 'asc') {
      return <ArrowUpwardIcon fontSize="small" />;
    } else {
      return <ArrowDownwardIcon fontSize="small" />;
    }
  }

  renderSaveTagModal() {
    const { classes, tags } = this.props;
    const { firebaseId } = this.state;

    const initialValues = {
      name: tags != null && firebaseId != null ? tags[firebaseId].name : '',
    };

    return (
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open
        onClose={this.handleCloseModal}
      >
        <div className={classes.modal}>
          <Formik
            key="saveTag"
            validateOnBlur
            enableReinitialize
            initialValues={initialValues}
            validationSchema={this.saveTagSchema}
            onSubmit={this.handleSaveSubmit}
          >
            {({ values, errors, touched }) => (
              <Form className={classes.form}>
                <Typography variant="h6" id="modal-title" gutterBottom>
                  {'Save Tag'}
                </Typography>

                <TextInputField
                  label="Name*"
                  id="name"
                  touched={touched.name}
                  error={errors.name}
                  autoComplete="name"
                  className={classes.inputField}
                  flexDirection="column"
                />

                <Grid container direction="row" justify="center" alignItems="center">
                  <Button large color="white" onClick={this.handleCloseModal}>
                    Cancel
                  </Button>
                  <Button submit large color="dark">
                    {'Save'}
                  </Button>
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
      </Modal>
    );
  }

  renderDeleteTagModal() {
    const { firebaseId } = this.state;
    const { classes, tags } = this.props;
    let msg = `Are you sure you want to delete ${tags[firebaseId].name}?`;

    return (
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open
        onClose={this.handleCloseModal}
      >
        <div className={classNames(classes.modal, classes.smallModal)}>
          <Typography variant="h6" id="modal-title" gutterBottom>
            {msg}
          </Typography>
          <Grid container direction="row" justify="center" alignItems="center">
            <Button color="white" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button color="dark" onClick={this.handleConfirmDelete}>
              Yes, I am sure.
            </Button>
          </Grid>
        </div>
      </Modal>
    );
  }

  // Build the visible rows live from the store on every render (like the roster tag
  // picker) so the list never lags behind add/delete/load. firebaseId is attached here
  // for the edit/delete menu.
  getDisplayedTags = () => {
    const { tags } = this.props;
    const { searchQuery, order } = this.state;
    let rows = tags ? Object.entries(tags).map(([firebaseId, tag]) => ({ ...tag, firebaseId })) : [];
    if (searchQuery) {
      rows = new Fuse(rows, FUSE_OPTIONS).search(searchQuery);
    }
    return this.sortBy(rows, order);
  };

  render() {
    const { classes } = this.props;
    const { isSaveModal, isDeleteModal, loadingTags, searchQuery } = this.state;
    const displayedTags = this.getDisplayedTags();

    return (
      <React.Fragment>
        {this.renderSearchBar()}
        <p></p>

        {this.props.profile &&
          this.props.profile.featureFlags &&
          this.props.profile.featureFlags.hb1416Tracker === true && (
            <Grid container direction="row" justify="space-between" alignItems="center" style={{ marginBottom: 8 }}>
              <Button color="dark" onClick={this.handleRecompute}>
                {this.state.recomputing ? 'Recomputing HB 1416...' : 'Recompute HB 1416 tags'}
              </Button>
              {this.state.recomputeMsg && (
                <Typography variant="body2" style={{ marginLeft: 12, flex: 1 }}>
                  {this.state.recomputeMsg}
                </Typography>
              )}
            </Grid>
          )}

        <Grid container direction="column" justify="flex-start" alignItems="center">
          {this.renderStatus()}
          <Grid container direction="row" justify="space-between" alignItems="center" item>
            <div onClick={this.onSortChange()} key="TagName" style={{ background: '#f8ffff', cursor: 'pointer' }}>
              Tag Name
              {this.renderSortIcon()}
            </div>

            <div className={classNames(classes.cell, classes.iconCell, classes.headerCell)}>
              <IconButton onClick={this.handleOpenNewModal}>
                <Add />
              </IconButton>
            </div>
          </Grid>

          {loadingTags && displayedTags.length === 0 && (
            <Typography variant="body1" style={{ marginTop: 12 }}>
              Loading tags...
            </Typography>
          )}
          {!loadingTags && displayedTags.length === 0 && (
            <Typography variant="body1" style={{ marginTop: 12 }}>
              {searchQuery ? 'No matching tags.' : 'No tags yet.'}
            </Typography>
          )}

          {displayedTags.map((tag, index) => (
            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              key={tag.firebaseId || index}
              item
            >
              <div className={classNames(classes.cell, classes.smallCell)}>{tag.name}</div>
              <div
                className={classNames(classes.cell, classes.iconCell)}
                style={{ background: 'rgba(255,255,255,0.0)' }}
              >
                {this.renderEditMenu(tag.firebaseId)}
              </div>
            </Grid>
          ))}

          {isSaveModal && this.renderSaveTagModal()}
          {isDeleteModal && this.renderDeleteTagModal()}
        </Grid>
      </React.Fragment>
    );
  }
}
export default withStyles(styles)(ManageOrgTags);
